import { ModuleTypeEnum, ModuleColumnDefinition } from '../module';
import { FieldTypeEnum } from '../custom-field.types';
import { Lead } from '../lead';
import { STANDARD_COLUMN_WIDTHS, getFieldTypeWidth } from '../column-widths';

/**
 * Standard column definitions for the Lead module
 */
export const LEAD_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'company',
    name: 'company' as keyof Lead,
    label: 'Company',
    description: 'Company name',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'firstName',
    name: 'firstName' as keyof Lead,
    label: 'First Name',
    description: 'First name of the lead',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'lastName',
    name: 'lastName' as keyof Lead,
    label: 'Last Name',
    description: 'Last name of the lead',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'email',
    name: 'email' as keyof Lead,
    label: 'Email',
    description: 'Email address of the lead',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'phone',
    name: 'phone' as keyof Lead,
    label: 'Phone',
    description: 'Phone number of the lead',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'website',
    name: 'website' as keyof Lead,
    label: 'Website',
    description: 'Company website URL',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.LINK,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'linkedinUrl',
    name: 'linkedinUrl' as keyof Lead,
    label: 'LinkedIn',
    description: 'LinkedIn profile URL',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 6,
    fieldType: FieldTypeEnum.enum.LINK,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'industry',
    name: 'industry' as keyof Lead,
    label: 'Industry',
    description: 'Industry classification',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'companySize',
    name: 'companySize' as keyof Lead,
    label: 'Company Size',
    description: 'Employee count range',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'country',
    name: 'country' as keyof Lead,
    label: 'Country',
    description: 'Geographic location',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 9,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'city',
    name: 'city' as keyof Lead,
    label: 'City',
    description: 'City location',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'timezone',
    name: 'timezone' as keyof Lead,
    label: 'Timezone',
    description: 'Timezone for scheduling',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 11,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'referredBy',
    name: 'referredBy' as keyof Lead,
    label: 'Referred By',
    description: 'Referral source or person',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 12,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'stage',
    name: 'stage' as keyof Lead,
    label: 'Stage',
    description: 'Lead pipeline stage',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 13,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'tags',
    name: 'tags' as keyof Lead,
    label: 'Tags',
    description: 'Tags for organizing and categorizing the lead',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 14,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'assignedToId',
    name: 'assignedToId' as keyof Lead,
    label: 'Assigned To',
    description: 'User assigned to this lead',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 15,
    fieldType: FieldTypeEnum.enum.USER,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'createdById',
    name: 'createdById' as keyof Lead,
    label: 'Created By',
    description: 'User who created this lead',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 98,
    fieldType: FieldTypeEnum.enum.USER,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof Lead,
    label: 'Created Date',
    description: 'Date lead was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 99,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'updatedAt',
    name: 'updatedAt' as keyof Lead,
    label: 'Last Updated',
    description: 'Date lead was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 100,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
];

/**
 * Helper to combine standard and custom fields
 */
export function getLeadColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>
): ModuleColumnDefinition[] {
  const standardColumns = LEAD_STANDARD_COLUMNS.map(column => ({
    ...column,
    isVisible: visibilityPreferences?.[column.id] !== undefined
      ? !!visibilityPreferences[column.id]
      : column.isVisible,
  }));

  const customColumns = customFields.map((field, index) => {
    const widthSettings = getFieldTypeWidth(field.fieldType);

    // Parse dropdown options from settings if available
    const fieldOptions = (field.fieldType === 'DROPDOWN' || field.fieldType === 'MULTISELECT')
      && field.settings?.options && Array.isArray(field.settings.options)
      ? field.settings.options.map((opt: any) => {
          if (typeof opt === 'string') return { value: opt, label: opt };
          return { value: String(opt.value || opt.id || opt.label || ''), label: String(opt.label || opt.name || opt.value || '') };
        })
      : undefined;

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible: visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
        ? !!visibilityPreferences[`custom_${field.fieldName}`]
        : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index, // Custom fields come after tags (order 90)
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
      ...(fieldOptions ? { options: fieldOptions } : {}),
    };
  }) as ModuleColumnDefinition[];

  return [...standardColumns, ...customColumns];
}

export function getLeadModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.LEAD;
}
